/**
 * Durable deletion-cleanup queue (1.63.x correctness release, stage 1.1).
 *
 * `deleteAppraisalPhotos()` removes the durable local record, the Storage
 * object and the exact database reference. The first two are local; the last
 * two need the network. A driver deleting a photo in a signal blackspot would
 * therefore keep a stale server reference forever — the exact condition that
 * blocked J-1072's POD ("3 referenced / 2 in Storage") — because the failure
 * was swallowed.
 *
 * The deletion INTENT is therefore persisted locally the moment server-side
 * cleanup cannot be completed, and drained by the existing upload-queue drain
 * (poll / reconnect / resume / foreground) and before a completion is queued.
 *
 * Deliberate constraints:
 *  - localStorage, exactly like `completionWorkflowQueue` — no new database
 *    table, no photo ledger, no IndexedDB schema bump.
 *  - EXACT paths only. A replacement photo has a different timestamped path
 *    and can never be pruned by a pending cleanup for the photo it replaced.
 *  - Idempotent: pruning an already-pruned path is a no-op that succeeds, and
 *    re-enqueueing the same path merges rather than duplicating.
 *  - Never restores or re-uploads a deleted photo, never blocks the UI.
 *  - A repeatedly failing record is RETAINED (backed off), never dropped.
 */

import { prunePhotoReferences, normalisePhotoPath } from "@/lib/appraisalPhotoReferences";
import { supabase } from "@/integrations/supabase/client";
import { isKnownOffline } from "@/lib/connectivity";
import { logOfflineEvent } from "@/lib/offlineTelemetry";
import { captureError } from "@/lib/observability";

const STORAGE_KEY = "tm_photo_reference_cleanups";
const BUCKET = "appraisal-photos";
/** Exponential backoff between drain attempts for one record (capped). */
const BASE_BACKOFF_MS = 30_000;
const MAX_BACKOFF_MS = 15 * 60_000;
/** Safety bound so a corrupt device cannot grow the key without limit. */
const MAX_RECORDS = 200;

export interface PhotoReferenceCleanup {
  appraisalId: string;
  /** Exact Storage object paths to remove and prune. Never prefixes. */
  paths: string[];
  /** Durable-record ids the deletion covered, where known (diagnostics + race guard). */
  localIds: string[];
  createdAt: number;
  attempts: number;
  lastAttemptAt?: number;
  lastError?: string;
  nextAttemptAt?: number;
}

function read(): PhotoReferenceCleanup[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is PhotoReferenceCleanup =>
        !!r && typeof r.appraisalId === "string" && Array.isArray(r.paths),
    );
  } catch {
    return [];
  }
}

function write(records: PhotoReferenceCleanup[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(-MAX_RECORDS)));
  } catch {
    /* quota/private mode — the in-flight prune attempt is still made */
  }
}

/**
 * Record that server-side cleanup for these exact paths is still outstanding.
 * Merges into an existing record for the same appraisal instead of duplicating.
 */
export function enqueuePhotoReferenceCleanup(params: {
  appraisalId: string;
  paths: readonly (string | null | undefined)[];
  localIds?: readonly (string | null | undefined)[];
  error?: unknown;
}): void {
  const paths = Array.from(
    new Set(params.paths.map(normalisePhotoPath).filter((p): p is string => !!p)),
  );
  if (!params.appraisalId || paths.length === 0) return;

  const localIds = Array.from(
    new Set((params.localIds || []).filter((id): id is string => typeof id === "string" && !!id)),
  );
  const records = read();
  const existing = records.find((r) => r.appraisalId === params.appraisalId);
  if (existing) {
    existing.paths = Array.from(new Set([...existing.paths, ...paths]));
    existing.localIds = Array.from(new Set([...(existing.localIds || []), ...localIds]));
    // A newly-deleted photo must be attempted promptly, not held behind the
    // backoff earned by an older failure in the same appraisal.
    existing.nextAttemptAt = 0;
  } else {
    records.push({
      appraisalId: params.appraisalId,
      paths,
      localIds,
      createdAt: Date.now(),
      attempts: 0,
    });
  }
  write(records);

  try {
    logOfflineEvent("photo_reference_cleanup_deferred", {
      appraisalId: params.appraisalId,
      store: "appraisal_photo_reference_cleanup",
      count: paths.length,
      paths,
      error: params.error,
    });
  } catch { /* telemetry must never break a delete */ }
}

/** Outstanding cleanup records (diagnostics / tests). */
export function getPhotoReferenceCleanups(): PhotoReferenceCleanup[] {
  return read();
}

/**
 * Cheap synchronous check. Callers on the upload path use this so the drain is
 * not even awaited when nothing is outstanding — the normal capture/upload flow
 * is byte-for-byte unchanged when no photo has been deleted offline.
 */
export function hasPhotoReferenceCleanups(): boolean {
  return read().length > 0;
}

/** True when this exact path is still awaiting server-side cleanup. */
export function hasPendingPhotoReferenceCleanup(appraisalId: string, path: string): boolean {
  const target = normalisePhotoPath(path);
  if (!target) return false;
  return read().some((r) => r.appraisalId === appraisalId && r.paths.includes(target));
}

/** Test helper — clears the durable queue. */
export function resetPhotoReferenceCleanupQueue(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

let draining = false;

/**
 * Attempt outstanding server-side cleanup. Safe to call from any drain trigger:
 * re-entrant calls return immediately, offline calls are a no-op, and a failing
 * record is retained with an exponential backoff rather than lost.
 */
export async function drainPhotoReferenceCleanups(
  options: { appraisalId?: string; force?: boolean } = {},
): Promise<number> {
  if (draining) return 0;
  const records = read();
  if (records.length === 0) return 0;
  if (!options.force && isKnownOffline()) return 0;

  draining = true;
  let cleared = 0;
  try {
    const now = Date.now();
    const remaining: PhotoReferenceCleanup[] = [];
    for (const record of records) {
      const due = (record.nextAttemptAt ?? 0) <= now;
      const scoped = !options.appraisalId || record.appraisalId === options.appraisalId;
      if (!due || !scoped) { remaining.push(record); continue; }

      // Storage removal first (best effort — an absent object is the desired
      // end state and must never hold the reference prune back).
      try { await supabase.storage.from(BUCKET).remove(record.paths); } catch { /* ignore */ }

      try {
        await prunePhotoReferences(record.appraisalId, record.paths);
        cleared += 1;
        logOfflineEvent("photo_reference_cleanup_completed", {
          appraisalId: record.appraisalId,
          store: "appraisal_photo_reference_cleanup",
          count: record.paths.length,
          paths: record.paths,
          attempts: record.attempts + 1,
        });
      } catch (error) {
        const attempts = record.attempts + 1;
        remaining.push({
          ...record,
          attempts,
          lastAttemptAt: now,
          lastError: error instanceof Error ? error.message : String(error),
          nextAttemptAt: now + Math.min(BASE_BACKOFF_MS * 2 ** (attempts - 1), MAX_BACKOFF_MS),
        });
        try {
          captureError("appraisal.photo.reference_cleanup", error, {
            severity: "warn",
            metadata: { appraisalId: record.appraisalId, attempts, paths: record.paths },
          });
        } catch { /* ignore */ }
      }
    }
    write(remaining);
  } finally {
    draining = false;
  }
  return cleared;
}
